<template>
  <section class="section container">
    <div class="container mt-0 mb-6">
      <h1 class="title is-4 mb-3 is-pulled-right">
        {{ $t("archive settings") }}
      </h1>
    </div>

    <!-- Archive Configuration -->
    <div class="columns mt-5">
      <div class="column">
        <h1 class="title is-5">{{ $t("general settings") }}</h1>

        <!-- Maximum file size -->
        <div class="field">
          <label class="label">{{ $t("maximum file size") }} (MB)</label>
          <div class="control">
            <input
              type="number"
              class="input"
              v-model="meta.max_file_size_mb"
              :placeholder="$t('enter maximum file size')"
              min="1"
              max="100"
            />
          </div>
          <p class="help">{{ $t("maximum file size help") }}</p>
        </div>

        <!-- Allowed file types -->
        <div class="field">
          <label class="label">{{ $t("allowed file types") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="meta.allowed_file_types"
              :placeholder="$t('enter allowed file types')"
              rows="3"
            ></textarea>
          </div>
          <p class="help">{{ $t("allowed file types help") }}</p>
        </div>

        <!-- Auto-create categories -->
        <div class="field">
          <input
            class="is-checkradio"
            type="checkbox"
            id="chk_auto_create_categories"
            v-model="meta.auto_create_categories"
          />
          <label for="chk_auto_create_categories">{{
            $t("auto create categories")
          }}</label>
          <p class="help">{{ $t("auto create categories help") }}</p>
        </div>

        <!-- Default category -->
        <div class="field" v-if="meta.auto_create_categories">
          <generic-dropdown
            :title="$t('default category')"
            :placeholder="$t('select category')"
            :is_multiple="false"
            url="/archive/category/catalog"
            name_field="name"
            value_field="category_id"
            @selected="category_selected"
            ref="default_category_dropdown"
          />
        </div>

        <!-- Storage settings -->
        <h1 class="title is-5 mt-6">{{ $t("storage settings") }}</h1>

        <generic-dropdown
          :title="$t('drive')"
          :placeholder="$t('select drive')"
          :is_multiple="false"
          url="/drive/catalog"
          name_field="name"
          value_field="drive_id"
          @selected="drive_selected"
          ref="drive_dropdown"
        />

        <!-- Storage path -->
        <div class="field">
          <label class="label">{{ $t("storage path") }}</label>
          <div class="control">
            <input
              type="text"
              class="input"
              v-model="meta.storage_path"
              :placeholder="$t('enter storage path')"
              readonly
            />
          </div>
          <p class="help">{{ $t("storage path help") }}</p>
        </div>

        <!-- Backup settings -->
        <div class="field">
          <input
            class="is-checkradio"
            type="checkbox"
            id="chk_enable_backup"
            v-model="meta.enable_backup"
          />
          <label for="chk_enable_backup">{{
            $t("enable automatic backup")
          }}</label>
        </div>

        <!-- Backup frequency -->
        <div class="field" v-if="meta.enable_backup">
          <label class="label">{{ $t("backup frequency") }}</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="meta.backup_frequency">
                <option value="daily">{{ $t("daily") }}</option>
                <option value="weekly">{{ $t("weekly") }}</option>
                <option value="monthly">{{ $t("monthly") }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Security settings -->
        <h1 class="title is-5 mt-6">{{ $t("security settings") }}</h1>

        <!-- Virus scanning -->
        <div class="field">
          <input
            class="is-checkradio"
            type="checkbox"
            id="chk_virus_scan"
            v-model="meta.enable_virus_scan"
          />
          <label for="chk_virus_scan">{{ $t("enable virus scanning") }}</label>
          <p class="help">{{ $t("virus scanning help") }}</p>
        </div>

        <!-- Access logging -->
        <div class="field">
          <input
            class="is-checkradio"
            type="checkbox"
            id="chk_access_log"
            v-model="meta.enable_access_log"
          />
          <label for="chk_access_log">{{ $t("enable access logging") }}</label>
          <p class="help">{{ $t("access logging help") }}</p>
        </div>

        <!-- File retention -->
        <div class="field">
          <label class="label"
            >{{ $t("file retention period") }} ({{ $t("days") }})</label
          >
          <div class="control">
            <input
              type="number"
              class="input"
              v-model="meta.retention_days"
              :placeholder="$t('enter retention days')"
              min="1"
              max="3650"
            />
          </div>
          <p class="help">{{ $t("file retention help") }}</p>
        </div>
      </div>
    </div>

    <!-- Save button -->
    <div class="container mt-6">
      <div class="field mt-5">
        <div class="control">
          <button
            class="button is-rounded is-primary"
            @click="save"
            :class="{ 'is-loading': saving }"
          >
            <span class="icon"><i class="fa fa-save"></i></span>
            <span>{{ $t("save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  save_configuration,
  get_configuration,
  success_message,
  error_message,
} from "@/utils";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";

export default {
  name: "ArchiveSettings",
  components: { GenericDropdown },
  data() {
    return {
      saving: false,
      meta: {
        max_file_size_mb: 50,
        allowed_file_types:
          "pdf,doc,docx,xls,xlsx,ppt,pptx,jpg,jpeg,png,gif,txt,rtf,zip,rar",
        auto_create_categories: true,
        default_category_id: null,
        storage_path: "/uploads/archive",
        enable_backup: false,
        backup_frequency: "weekly",
        enable_virus_scan: false,
        enable_access_log: true,
        retention_days: 365,
      },
    };
  },
  methods: {
    save() {
      let v = this;
      v.saving = true;

      save_configuration(this, v.meta)
        .then(() => {
          success_message(v.$t("configuration saved"));
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          v.saving = false;
        });
    },

    category_selected(category) {
      if (category) this.meta.default_category_id = category.category_id;
    },
    drive_selected(drive) {
      if (drive) this.meta.drive_id = drive.drive_id;
    },

    load_configuration() {
      let v = this;

      get_configuration(this)
        .then((conf) => {
          if (conf) {
            v.meta = { ...v.meta, ...conf };

            // Set dropdown values after loading
            v.$nextTick(() => {
              if (
                conf.default_category_id &&
                v.$refs.default_category_dropdown
              ) {
                v.$refs.default_category_dropdown.set_value(
                  conf.default_category_id
                );
              }

              if (conf.drive_id && v.$refs.drive_dropdown) {
                v.$refs.drive_dropdown.set_value(conf.drive_id);
              }
            });
          }
        })
        .catch((error) => {
          error_message(error);
        });
    },
  },
  created() {
    this.load_configuration();
  },
};
</script>

<style scoped>
.help {
  font-size: 0.75rem;
  color: #6b7280;
}

.title.is-5 {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
  margin-bottom: 1.5rem;
}
</style>
